/**
 * Middleware for the Call Management & UX feature.
 *
 * Listens for WebSocket events from InterpreterQueueService and
 * dispatches corresponding Redux actions.
 */
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import { queueService } from '../interpreter-queue/InterpreterQueueService';
import { hideNotification, showNotification } from '../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../notifications/constants';
import { getPersistentJson, removePersistentItem, setPersistentItem } from '../vrs-auth/storage';
import { callWaitingIncoming, callOnHold, callOffHold, chatMessageReceived, heldCallCleared, heldCallSet, chatHistoryLoaded, preferencesUpdated, p2pTargetDnd } from './actions';
import { holdNoticeTitle } from './callWaitingSwitch';
import { interpreterChangedNotice } from './interpreterChange';
import { HELD_CALL_CLEARED, HELD_CALL_SET, CONFERENCE_INVITE, CONFERENCE_ADD_RINGING, CONFERENCE_REMOVED, CONFERENCE_PARTICIPANT_REMOVED } from './actionTypes';
// The queue listener generation of the listeners below. A sign-out removes
// every queue listener (queueService.disconnect) while the app stays open, so
// the next sign-in registers them again (Robert, 2026-10-29).
let _listenersFor;
/**
 * The call this device holds is kept across page loads (the web opens each
 * room with a full page load) and app restarts (vrs_held_call), so its bar,
 * Home's Resume and the web portal's row survive them (Robert, 2026-10-12:
 * "add hold screen to web portal").
 */
export const HELD_CALL_STORAGE_KEY = 'vrs_held_call';
MiddlewareRegistry.register(store => next => action => {
    const result = next(action);
    if (action.type === HELD_CALL_SET && action.heldCall?.callId) {
        setPersistentItem(HELD_CALL_STORAGE_KEY, JSON.stringify(action.heldCall));
    }
    else if (action.type === HELD_CALL_CLEARED) {
        const stored = getPersistentJson(HELD_CALL_STORAGE_KEY);
        if (!action.callId || stored?.callId === action.callId) {
            removePersistentItem(HELD_CALL_STORAGE_KEY);
        }
    }
    if (_listenersFor !== queueService.listenerGeneration()) {
        _listenersFor = queueService.listenerGeneration();
        initializeCallManagementListeners(store);
    }
    return result;
});
function initializeCallManagementListeners(store) {
    if (!queueService?.on) {
        return;
    }
    // A held call kept from before this page or launch comes back once the
    // queue is signed in (native storage is loaded by then), and is dropped
    // if it ended meanwhile (held_call_status).
    queueService.on('authenticated', () => {
        const kept = getPersistentJson(HELD_CALL_STORAGE_KEY);
        const held = store.getState()['features/call-management']?.heldCall;
        if (!held && kept?.callId && kept.roomName) {
            store.dispatch(heldCallSet(kept));
        }
        const callId = held?.callId || kept?.callId;
        if (callId) {
            queueService.checkHeldCall(callId);
        }
    });
    queueService.on('heldCallStatus', (data) => {
        if (!data.live && store.getState()['features/call-management']?.heldCall?.callId === data.callId) {
            store.dispatch(heldCallCleared(data.callId));
        }
    });
    // Call Waiting
    queueService.on('callWaitingIncoming', (data) => {
        store.dispatch(callWaitingIncoming(data));
    });
    queueService.on('callWaitingResponded', (_data) => {
        // Handled by reducer via the action dispatched from the component
    });
    // Call Hold: someone else in this call held it, or is back. Said in the
    // call (the room shows notifications); a hold stays up until they return.
    queueService.on('callOnHold', (data) => {
        store.dispatch(callOnHold(data));
        showHoldNotice(store, true, data);
    });
    queueService.on('callOffHold', (data) => {
        store.dispatch(callOffHold(data));
        showHoldNotice(store, false, data);
    });
    // A call this device held ends while it waits: nothing to resume.
    const heldCallEnded = (data) => {
        const callId = data && typeof data === 'object' ? data.callId : undefined;
        if (typeof callId === 'string' && callId
            && store.getState()['features/call-management']?.heldCall?.callId === callId) {
            store.dispatch(heldCallCleared(callId));
        }
    };
    queueService.on('callEnded', heldCallEnded);
    queueService.on('p2pCallEnded', heldCallEnded);
    // The call changed interpreter (an interpreter-to-interpreter transfer):
    // who is interpreting now, said in the call.
    queueService.on('callInterpreterChanged', (data) => {
        showInterpreterChangedNotice(store, data);
    });
    // Conference
    queueService.on('conferenceInvite', (data) => {
        store.dispatch({ type: CONFERENCE_INVITE, data });
    });
    queueService.on('conferenceAddRinging', (data) => {
        store.dispatch({ type: CONFERENCE_ADD_RINGING, data });
    });
    queueService.on('conferenceRemoved', (data) => {
        store.dispatch({ type: CONFERENCE_REMOVED, data });
    });
    queueService.on('conferenceParticipantRemoved', (data) => {
        store.dispatch({ type: CONFERENCE_PARTICIPANT_REMOVED, data });
    });
    // Chat
    queueService.on('chatMessage', (data) => {
        store.dispatch(chatMessageReceived(data));
    });
    queueService.on('chatHistory', (data) => {
        store.dispatch(chatHistoryLoaded(data));
    });
    // Preferences
    queueService.on('preferencesUpdated', (data) => {
        store.dispatch(preferencesUpdated(data));
        if (typeof data.camera_default_off === 'boolean') {
            setPersistentItem('vrs_incoming_video_enabled', JSON.stringify(!data.camera_default_off));
        }
        // Apply dark mode on <body>
        if (data.dark_mode) {
            applyDarkMode(data.dark_mode);
        }
    });
    // P2P Target DND
    queueService.on('p2pTargetDnd', (data) => {
        store.dispatch(p2pTargetDnd(data));
    });
}
function holdNoticeUid(callId) {
    return `call-hold-${callId}`;
}
/**
 * The hold notice for the call this device is in (its stored call); a hold
 * on a call this device is not showing says nothing here.
 */
function showHoldNotice(store, onHold, data) {
    const callId = typeof data?.callId === 'string' ? data.callId : '';
    if (!callId || getPersistentJson('vrs_active_call')?.callId !== callId) {
        return;
    }
    store.dispatch(hideNotification(holdNoticeUid(callId)));
    store.dispatch(showNotification({
        title: holdNoticeTitle(onHold, data),
        uid: onHold ? holdNoticeUid(callId) : `${holdNoticeUid(callId)}-back`
    }, onHold ? NOTIFICATION_TIMEOUT_TYPE.STICKY : NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
}
/** "Sam is interpreting." for the call this device is in (interpreterChange.ts). */
function showInterpreterChangedNotice(store, data) {
    const notice = interpreterChangedNotice(getPersistentJson('vrs_active_call'), data);
    if (!notice) {
        return;
    }
    setPersistentItem('vrs_active_call', JSON.stringify(notice.nextStored));
    store.dispatch(showNotification({ title: notice.title, uid: notice.uid }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
}
/**
 * Apply dark mode class to the document body.
 */
function applyDarkMode(mode) {
    if (typeof document === 'undefined') {
        return;
    }
    const body = document.body;
    body.classList.remove('vrs-dark', 'vrs-light');
    if (mode === 'dark') {
        body.classList.add('vrs-dark');
    }
    else if (mode === 'light') {
        body.classList.add('vrs-light');
    }
    else {
        // system — detect OS preference
        const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)')?.matches;
        body.classList.add(prefersDark ? 'vrs-dark' : 'vrs-light');
    }
}
// Export for use by the dark mode component
export { applyDarkMode };
